/**
 * Sloupcový (kompaktní) tvar tabulky procesů pro přenos po drátě.
 *
 * PROČ: Socket.IO 4 / Engine.IO 4 má `perMessageDeflate` VYPNUTÝ (default, a projekt ho
 * nezapíná — viz index.ts), takže po drátě jde nekomprimovaný JSON. Pole objektů opakuje
 * jména klíčů u každého řádku; sloupcový tvar (`cols` + `rows: unknown[][]`) ušetří ~55 %
 * bajtů bez jakéhokoli stavu na obou stranách.
 *
 * Pořadí sloupců je součástí kontraktu — `unpack` čte podle `cols`, ne podle indexu,
 * takže přidání sloupce nerozbije staršího klienta (chybějící pole dostane default).
 */

import type { SysmonProcess, SysmonProcState } from "@globals/sysmon/types";

export const PROC_COLUMNS = [
  "pid",
  "ppid",
  "name",
  "cmd",
  "user",
  "uid",
  "state",
  "threads",
  "cpuPercent",
  "memRssB",
  "memPercent",
  "memVirtB",
  "cpuTimeS",
  "ageS",
  "nice",
  "lastCore",
  "kernel",
  "depth",
] as const;

export type SysmonProcColumn = (typeof PROC_COLUMNS)[number];

export interface SysmonProcWire {
  cols: SysmonProcColumn[];
  rows: (string | number | boolean | null)[][];
}

const VALID_STATES: SysmonProcState[] = ["R", "S", "D", "Z", "T", "t", "X", "I", "?"];

const pick = (p: SysmonProcess, col: SysmonProcColumn): string | number | boolean | null => {
  switch (col) {
    case "pid":
      return p.pid;
    case "ppid":
      return p.ppid;
    case "name":
      return p.name;
    case "cmd":
      return p.cmd;
    case "user":
      return p.user;
    case "uid":
      return p.uid;
    case "state":
      return p.state;
    case "threads":
      return p.threads;
    case "cpuPercent":
      return p.cpuPercent;
    case "memRssB":
      return p.memRssB;
    case "memPercent":
      return p.memPercent;
    case "memVirtB":
      return p.memVirtB;
    case "cpuTimeS":
      return p.cpuTimeS;
    case "ageS":
      return p.ageS;
    case "nice":
      return p.nice;
    case "lastCore":
      return p.lastCore;
    case "kernel":
      return p.kernel;
    case "depth":
      return p.depth ?? null;
    default:
      return null;
  }
};

export const packProcesses = (rows: readonly SysmonProcess[]): SysmonProcWire => ({
  cols: [...PROC_COLUMNS],
  rows: rows.map((p) => PROC_COLUMNS.map((c) => pick(p, c))),
});

const num = (v: unknown, fallback = 0): number => (typeof v === "number" && Number.isFinite(v) ? v : fallback);

const str = (v: unknown, fallback = ""): string => (typeof v === "string" ? v : fallback);

export const unpackProcesses = (wire: SysmonProcWire): SysmonProcess[] => {
  const index = new Map<SysmonProcColumn, number>();
  wire.cols.forEach((c, i) => index.set(c, i));
  const at = (row: (string | number | boolean | null)[], col: SysmonProcColumn): unknown => {
    const i = index.get(col);
    return i === undefined ? undefined : row[i];
  };

  return wire.rows.map((row) => {
    const rawState = str(at(row, "state"), "?");
    const state = (VALID_STATES as string[]).includes(rawState) ? (rawState as SysmonProcState) : "?";
    const depthRaw = at(row, "depth");
    const lastCoreRaw = at(row, "lastCore");

    const proc: SysmonProcess = {
      pid: num(at(row, "pid")),
      ppid: num(at(row, "ppid")),
      name: str(at(row, "name")),
      cmd: str(at(row, "cmd")),
      user: str(at(row, "user")),
      uid: num(at(row, "uid"), -1),
      state,
      threads: num(at(row, "threads"), 1),
      cpuPercent: num(at(row, "cpuPercent")),
      memRssB: num(at(row, "memRssB")),
      memPercent: num(at(row, "memPercent")),
      memVirtB: num(at(row, "memVirtB")),
      cpuTimeS: num(at(row, "cpuTimeS")),
      ageS: num(at(row, "ageS")),
      nice: num(at(row, "nice")),
      lastCore: typeof lastCoreRaw === "number" ? lastCoreRaw : null,
      kernel: at(row, "kernel") === true,
    };
    if (typeof depthRaw === "number") proc.depth = depthRaw;
    return proc;
  });
};
